import { useMemo, useState, useEffect, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import { modalSecondaryButtonStyle } from '../styles/buttonStyles'
import GenericListFilter from './GenericListFilter'
import useGenericListFilter from '../hooks/useGenericListFilter'
import StatsOverviewModal from './stats/StatsOverviewModal'
import StatsTripsModal from './stats/StatsTripsModal'
import StatsCostsModal from './stats/StatsCostsModal'
import StatsEmissionsModal from './stats/StatsEmissionsModal'
import StatsVehiclesModal from './stats/StatsVehiclesModal'
import StatsEvOverviewModal from './stats/StatsEvOverviewModal'
import StatsEvVehiclesModal from './stats/StatsEvVehiclesModal'
import ExportButton from '../components/ExportButton'
import ContactModal from './ContactModal'
import { calculateFuelUsedLiters } from '../helpers/calculateConsumption'
import {
  exportCurrentStatsCSV,
  exportCurrentStatsXLS,
  exportCurrentStatsPDF
} from '../utils/statisticsExport'

const chartColors = ['#4F81BD', '#6FA16F', '#C27C59', '#9B6FAE', '#BFA65A']

const tabs = [
  { key: 'summary', labelKey: 'TRIPLOG.STATISTICS.SUMMARY_TAB' },
  { key: 'trips', labelKey: 'TRIPLOG.STATISTICS.TRIPS_TAB' },
  { key: 'costs', labelKey: 'TRIPLOG.STATISTICS.COSTS_TAB' },
  { key: 'emissions', labelKey: 'TRIPLOG.STATISTICS.EMISSIONS_TAB' },
  { key: 'vehicles', labelKey: 'TRIPLOG.STATISTICS.VEHICLES_TAB' }
]

const StatsModal = ({ open, onClose, userId }) => {
  const { t, i18n } = useTranslation()
  const [activeTab, setActiveTab] = useState('summary')
  const [statsMode, setStatsMode] = useState('fuel')
  const [hoverBtn, setHoverBtn] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [openContact, setOpenContact] = useState(false)
  const [contactLinkHover, setContactLinkHover] = useState(false)

  const modalRef = useRef(null)
  const dragRef = useRef(null)

  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return
      if (openContact) return
      onClose()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, openContact, onClose])

  useEffect(() => {
    if (!open) {
      setActiveTab('summary')
      setStatsMode('fuel')
      setHoverBtn(null)
      setPressedBtn(null)
      setModalPosition({ x: 0, y: 0 })
    }
  }, [open])

  const { data, isLoading } = useQuery({
    queryKey: ['stats-modal-data', userId],
    enabled: open && !!userId,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const [tripsRes, receiptsRes, servicesRes, vehiclesRes] = await Promise.all([
        fetchWithAuth('/triplog/trips'),
        fetchWithAuth(`/users/${userId}/receipts`),
        fetchWithAuth('/triplog/service-costs'),
        fetchWithAuth(`/users/${userId}/vehicles`)
      ])

      const trips = tripsRes.ok ? await tripsRes.json() : []
      const receipts = receiptsRes.ok ? await receiptsRes.json() : []
      const services = servicesRes.ok ? await servicesRes.json() : []
      const vehicles = vehiclesRes.ok ? await vehiclesRes.json() : []

      return {
        trips: Array.isArray(trips) ? trips : [],
        receipts: Array.isArray(receipts) ? receipts : [],
        services: Array.isArray(services) ? services : [],
        vehicles: Array.isArray(vehicles) ? vehicles : []
      }
    }
  })

  const trips = useMemo(() => (Array.isArray(data?.trips) ? data.trips : []), [data?.trips])
  const receipts = useMemo(() => (Array.isArray(data?.receipts) ? data.receipts : []), [data?.receipts])
  const services = useMemo(() => (Array.isArray(data?.services) ? data.services : []), [data?.services])
  const vehicles = useMemo(() => (Array.isArray(data?.vehicles) ? data.vehicles : []), [data?.vehicles])

  const {
    criteria: filterCriteria,
    setCriteria: setFilterCriteria
  } = useGenericListFilter(
    trips,
    (trip) => ({
      vehicleId: trip?.vehicleId,
      date: trip?.date
    }),
  )

  const filterLabels = useMemo(() => ({
    allVehicles: t('TRIPLOG.BUTTON.ALL_VEHICLES'),
    from: t('TRIPLOG.FILTER_FROM'),
    to: t('TRIPLOG.FILTER_TO'),
    month: t('TRIPLOG.FILTER_MONTH'),
    year: t('TRIPLOG.FILTER_YEAR'),
    clear: t('TRIPLOG.BUTTON.CLEAR')
  }), [t])

  const vehicleOptions = useMemo(() => {
    const electricVehicleIds = new Set(receipts.filter((receipt) => receipt?.fuel_type === 'electric').map((receipt) => String(receipt?.vehicleId)))
    const fuelVehicleIds = new Set(receipts.filter((receipt) => receipt?.fuel_type !== 'electric').map((receipt) => String(receipt?.vehicleId)))

    return vehicles
      .filter((vehicle) => {
        const id = String(vehicle.id)
        const hasElectricReceipt = electricVehicleIds.has(id)
        const hasFuelReceipt = fuelVehicleIds.has(id)
        const hasAnyReceipt = hasElectricReceipt || hasFuelReceipt

        if (!hasAnyReceipt) return true
        return statsMode === 'electric' ? hasElectricReceipt : hasFuelReceipt
      })
      .map((vehicle) => ({
        value: String(vehicle.id),
        label: vehicle.name || vehicle.model || vehicle.licensePlate || vehicle.spz || `#${vehicle.id}`
      }))
  }, [vehicles, receipts, statsMode])

  const selectedVehicleName = useMemo(() => {
    if (!filterCriteria?.vehicleId) return t('TRIPLOG.BUTTON.ALL_VEHICLES')

    const selected = vehicles.find((vehicle) => String(vehicle.id) === String(filterCriteria.vehicleId))
    if (!selected) return `#${filterCriteria.vehicleId}`

    const vehicleName = selected.name || selected.model || `#${selected.id}`
    const licensePlate = selected.licensePlate || selected.spz || ''

    return licensePlate ? `${vehicleName} (${licensePlate})` : vehicleName
  }, [filterCriteria?.vehicleId, t, vehicles])

  const stats = useMemo(() => {
    const toNum = (v) => {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    }

    const matchesFilter = (itemDate, vehicleId) => {
      const normalizedDate = itemDate ? String(itemDate).slice(0, 10) : null

      if (filterCriteria?.vehicleId && String(vehicleId) !== String(filterCriteria.vehicleId)) return false
      if (filterCriteria?.from && normalizedDate < filterCriteria.from) return false
      if (filterCriteria?.to && normalizedDate > filterCriteria.to) return false
      if (filterCriteria?.month && !normalizedDate?.startsWith(filterCriteria.month)) return false
      if (filterCriteria?.year && !normalizedDate?.startsWith(filterCriteria.year)) return false

      return true
    }

    const filteredTrips = trips.filter((trip) => matchesFilter(trip?.date, trip?.vehicleId))
    const filteredReceipts = receipts.filter((receipt) => matchesFilter(receipt?.date, receipt?.vehicleId))
    const filteredServices = services.filter((service) => matchesFilter(service?.date, service?.vehicleId))

    const filteredFuelReceipts = filteredReceipts.filter((receipt) => receipt?.fuel_type !== 'electric')
    const filteredElectricReceipts = filteredReceipts.filter((receipt) => receipt?.fuel_type === 'electric')
    const activeReceipts = statsMode === 'electric' ? filteredElectricReceipts : filteredFuelReceipts

    const electricVehicleIds = new Set(receipts.filter((receipt) => receipt?.fuel_type === 'electric').map((receipt) => String(receipt?.vehicleId)))
    const fuelVehicleIds = new Set(receipts.filter((receipt) => receipt?.fuel_type !== 'electric').map((receipt) => String(receipt?.vehicleId)))

    const activeTrips = filteredTrips.filter((trip) => {
      const vehicleId = String(trip?.vehicleId)
      const hasElectricReceipt = electricVehicleIds.has(vehicleId)
      const hasFuelReceipt = fuelVehicleIds.has(vehicleId)
      const hasAnyReceipt = hasElectricReceipt || hasFuelReceipt

      if (!hasAnyReceipt) return true
      return statsMode === 'electric' ? hasElectricReceipt : hasFuelReceipt
    })

    const activeVehicleIds = new Set(
      vehicles
        .filter((vehicle) => {
          const id = String(vehicle.id)
          const hasElectricReceipt = electricVehicleIds.has(id)
          const hasFuelReceipt = fuelVehicleIds.has(id)
          const hasAnyReceipt = hasElectricReceipt || hasFuelReceipt

          if (!hasAnyReceipt) return true
          return statsMode === 'electric' ? hasElectricReceipt : hasFuelReceipt
        })
        .map((vehicle) => String(vehicle.id))
    )

    const activeServices = filteredServices.filter((service) => activeVehicleIds.has(String(service?.vehicleId)))

    const totalTrips = activeTrips.length
    const totalKm = activeTrips.reduce((sum, trip) => sum + toNum(trip.distanceKm), 0)

    const totalReceiptCost = activeReceipts.reduce((sum, receipt) => sum + toNum(receipt.total_price), 0)
    const totalServiceCost = activeServices.reduce((sum, service) => sum + toNum(service.totalCost), 0)
    const totalCost = totalReceiptCost + totalServiceCost

    const totalFuel = statsMode === 'fuel'
      ? activeReceipts.reduce((sum, receipt) => sum + toNum(receipt.liters ?? receipt.units), 0)
      : 0

    const totalElectricKwh = statsMode === 'electric'
      ? activeReceipts.reduce((sum, receipt) => sum + toNum(receipt.units), 0)
      : 0

    const avgElectricPrice = totalElectricKwh > 0 ? totalReceiptCost / totalElectricKwh : 0
    const electricKwhPer100Km = totalKm > 0 ? (totalElectricKwh / totalKm) * 100 : 0
    const electricCostPer100Km = totalKm > 0 ? (totalReceiptCost / totalKm) * 100 : 0

    const chargingBreakdown = activeReceipts.reduce((acc, receipt) => {
      if (statsMode !== 'electric') return acc

      const type = receipt?.charging_type
      if (!['home', 'public', 'package'].includes(type)) return acc

      acc[type].kwh += toNum(receipt.units)
      acc[type].cost += toNum(receipt.total_price)

      return acc
    }, {
      home: { kwh: 0, cost: 0 },
      public: { kwh: 0, cost: 0 },
      package: { kwh: 0, cost: 0 }
    })

    const chargingStats = {
      home: { ...chargingBreakdown.home, percent: totalElectricKwh > 0 ? (chargingBreakdown.home.kwh / totalElectricKwh) * 100 : 0 },
      public: { ...chargingBreakdown.public, percent: totalElectricKwh > 0 ? (chargingBreakdown.public.kwh / totalElectricKwh) * 100 : 0 },
      package: { ...chargingBreakdown.package, percent: totalElectricKwh > 0 ? (chargingBreakdown.package.kwh / totalElectricKwh) * 100 : 0 }
    }

    const consumptionTotals = activeTrips.reduce((result, trip) => {
      if (statsMode !== 'fuel') return result

      const distanceKm = toNum(trip.distanceKm)
      const fuelUsedLiters = calculateFuelUsedLiters(trip, vehicles)

      if (distanceKm <= 0 || fuelUsedLiters === null) return result

      result.fuelUsedLiters += fuelUsedLiters
      result.measuredKm += distanceKm
      return result
    }, { fuelUsedLiters: 0, measuredKm: 0 })

    const avgConsumption = consumptionTotals.measuredKm > 0 ? (consumptionTotals.fuelUsedLiters / consumptionTotals.measuredKm) * 100 : 0
    const avgCostPerKm = totalKm > 0 ? totalCost / totalKm : 0

    const vehicleMap = vehicles.reduce((acc, vehicle) => {
      acc[String(vehicle.id)] = {
        id: vehicle.id,
        name: vehicle.name || vehicle.model || vehicle.licensePlate || vehicle.spz || `#${vehicle.id}`,
        licensePlate: vehicle.licensePlate || '',
        spz: vehicle.spz || '',
        license: vehicle.licensePlate || vehicle.spz || '-',
        trips: 0,
        km: 0,
        fuel: 0,
        electricKwh: 0,
        electricCost: 0,
        consumptionFuelUsed: 0,
        consumptionKm: 0,
        receiptCost: 0,
        serviceCost: 0,
        co2Kg: 0
      }

      return acc
    }, {})

    activeTrips.forEach((trip) => {
      const key = String(trip.vehicleId)
      if (!vehicleMap[key]) return

      const distance = toNum(trip.distanceKm)
      const fuelUsedLiters = statsMode === 'fuel' ? calculateFuelUsedLiters(trip, vehicles) : null
      const vehicle = vehicles.find((v) => String(v.id) === key)
      const co2PerKm = Number(vehicle?.emissionsCo2 || 0)

      vehicleMap[key].trips += 1
      vehicleMap[key].km += distance

      if (fuelUsedLiters !== null) {
        vehicleMap[key].consumptionFuelUsed += fuelUsedLiters
        vehicleMap[key].consumptionKm += distance
      }

      if (statsMode === 'fuel' && distance > 0 && co2PerKm > 0) {
        vehicleMap[key].co2Kg += (distance * co2PerKm) / 1000
      }
    })

    activeReceipts.forEach((receipt) => {
      const key = String(receipt.vehicleId)
      if (!vehicleMap[key]) return

      if (statsMode === 'electric') {
        vehicleMap[key].electricKwh += toNum(receipt.units)
        vehicleMap[key].electricCost += toNum(receipt.total_price)
        vehicleMap[key].receiptCost += toNum(receipt.total_price)
        return
      }

      vehicleMap[key].fuel += toNum(receipt.liters ?? receipt.units)
      vehicleMap[key].receiptCost += toNum(receipt.total_price)
    })

    activeServices.forEach((service) => {
      const key = String(service.vehicleId)
      if (!vehicleMap[key]) return
      vehicleMap[key].serviceCost += toNum(service.totalCost)
    })

    const totalCo2Kg = Object.values(vehicleMap).reduce((sum, vehicle) => sum + vehicle.co2Kg, 0)
    const co2PerKm = totalKm > 0 ? (totalCo2Kg * 1000) / totalKm : 0

    const vehiclesStats = Object.values(vehicleMap)
      .filter((vehicle) => {
        if (filterCriteria?.vehicleId && String(vehicle.id) !== String(filterCriteria.vehicleId)) return false
        return activeVehicleIds.has(String(vehicle.id))
      })
      .map((vehicle) => {
        const roundedKm = Number(vehicle.km.toFixed(1))
        const roundedFuel = Number(vehicle.fuel.toFixed(1))
        const vehicleAvgConsumption = vehicle.consumptionKm > 0
          ? Number(((vehicle.consumptionFuelUsed / vehicle.consumptionKm) * 100).toFixed(2))
          : 0

        const vehicleElectricKwhPer100Km = vehicle.km > 0 ? (vehicle.electricKwh / vehicle.km) * 100 : 0
        const vehicleElectricCostPer100Km = vehicle.km > 0 ? (vehicle.electricCost / vehicle.km) * 100 : 0
        const vehicleAvgElectricPrice = vehicle.electricKwh > 0 ? vehicle.electricCost / vehicle.electricKwh : 0

        return {
          ...vehicle,
          km: roundedKm,
          fuel: roundedFuel,
          electricKwh: Number(vehicle.electricKwh.toFixed(2)),
          electricCost: Number(vehicle.electricCost.toFixed(2)),
          electricKwhPer100Km: Number(vehicleElectricKwhPer100Km.toFixed(2)),
          electricCostPer100Km: Number(vehicleElectricCostPer100Km.toFixed(2)),
          avgElectricPrice: Number(vehicleAvgElectricPrice.toFixed(3)),
          totalCost: Number((vehicle.receiptCost + vehicle.serviceCost).toFixed(2)),
          avgConsumption: vehicleAvgConsumption,
          co2PerKm: vehicle.km > 0 ? (vehicle.co2Kg * 1000) / vehicle.km : 0
        }
      })
      .sort((a, b) => b.km - a.km)

    const chartVehicles = vehicles
      .filter((vehicle) => activeTrips.some((trip) => String(trip?.vehicleId) === String(vehicle?.id)))
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'sk'))

    const chartVehicleMap = chartVehicles.reduce((acc, vehicle, index) => {
      acc[String(vehicle.id)] = {
        vehicleId: String(vehicle.id),
        color: chartColors[index % chartColors.length],
        label: vehicle.name || vehicle.model || vehicle.licensePlate || vehicle.spz || `#${vehicle.id}`,
        licensePlate: vehicle.licensePlate || vehicle.spz || ''
      }

      return acc
    }, {})

    const monthMap = activeTrips.reduce((acc, trip) => {
      const rawDate = trip?.date ? String(trip.date).slice(0, 10) : ''
      if (!rawDate) return acc

      const [year, month] = rawDate.split('-')
      if (!year || !month) return acc

      const monthKey = `${year}-${month}`
      if (!acc[monthKey]) acc[monthKey] = {}

      const vehicleId = String(trip?.vehicleId ?? '')
      acc[monthKey][vehicleId] = (acc[monthKey][vehicleId] || 0) + toNum(trip?.distanceKm)

      return acc
    }, {})

    const getMonthRange = () => {
      let startYear
      let endYear

      if (filterCriteria?.year) {
        startYear = Number(filterCriteria.year)
        endYear = Number(filterCriteria.year)
      } else if (filterCriteria?.month) {
        startYear = Number(String(filterCriteria.month).slice(0, 4))
        endYear = startYear
      } else if (filterCriteria?.from || filterCriteria?.to) {
        const fromYear = filterCriteria?.from ? Number(String(filterCriteria.from).slice(0, 4)) : null
        const toYear = filterCriteria?.to ? Number(String(filterCriteria.to).slice(0, 4)) : null

        startYear = fromYear ?? toYear
        endYear = toYear ?? fromYear
      } else {
        startYear = new Date().getFullYear()
        endYear = startYear
      }

      if (!Number.isFinite(startYear) || !Number.isFinite(endYear)) {
        const currentYear = new Date().getFullYear()
        startYear = currentYear
        endYear = currentYear
      }

      if (startYear > endYear) {
        const tmp = startYear
        startYear = endYear
        endYear = tmp
      }

      const range = []

      for (let year = startYear; year <= endYear; year += 1) {
        for (let month = 1; month <= 12; month += 1) {
          range.push(`${year}-${String(month).padStart(2, '0')}`)
        }
      }

      return range
    }

    const monthKeys = getMonthRange()
    const statsLocale = i18n.language === 'ua' ? 'uk' : i18n.language

    const tripsChartData = monthKeys.map((monthKey) => {
      const monthDate = new Date(`${monthKey}-01`)
      const monthLabel = monthDate.toLocaleString(statsLocale, { month: 'short' })
      const perVehicle = monthMap[monthKey] || {}

      const segments = Object.entries(perVehicle)
        .filter(([, value]) => Number(value || 0) > 0)
        .map(([vehicleId, value]) => ({
          vehicleId,
          value: Number(value || 0),
          color: chartVehicleMap[vehicleId]?.color || chartColors[0],
          label: chartVehicleMap[vehicleId]?.label || `#${vehicleId}`
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'sk'))

      const total = segments.reduce((sum, segment) => sum + segment.value, 0)
      const tripCount = activeTrips.filter((trip) => String(trip?.date || '').slice(0, 7) === monthKey).length

      return { month: monthKey, monthLabel, total, tripCount, segments }
    })

    const tripsMaxValue = Math.max(...tripsChartData.map((item) => item.total), 0, 1)
    const tripsTicks = Array.from({ length: 7 }, (_, i) => Math.round((tripsMaxValue / 6) * i))

    const normalizedTripsChartData = tripsChartData.map((item) => ({
      ...item,
      segments: item.segments.map((segment) => ({
        ...segment,
        heightPercent: item.total > 0 ? (segment.value / tripsMaxValue) * 100 : 0
      }))
    }))

    const formatPeriodText = () => {
      const fromRaw = filterCriteria?.from || ''
      const toRaw = filterCriteria?.to || ''
      const monthRaw = filterCriteria?.month || ''
      const yearRaw = filterCriteria?.year || ''

      const format = (date) => {
        const month = date.toLocaleString(statsLocale, { month: 'short' }).toUpperCase()
        return `${month} ${date.getFullYear()}`
      }

      if (monthRaw) {
        const monthDate = new Date(`${monthRaw}-01`)
        if (!Number.isNaN(monthDate.getTime())) return format(monthDate)
      }

      if (yearRaw) return String(yearRaw)

      if (!fromRaw && !toRaw) {
        const periodDates = [...activeTrips, ...activeReceipts, ...activeServices]
          .map((item) => item?.date ? new Date(String(item.date).slice(0, 10)) : null)
          .filter((date) => date && !Number.isNaN(date.getTime()))
          .sort((a, b) => a - b)

        if (!periodDates.length) return '-'

        const from = format(periodDates[0])
        const to = format(periodDates[periodDates.length - 1])

        return from === to ? from : `${from} – ${to}`
      }

      const fromDate = fromRaw ? new Date(fromRaw) : null
      const toDate = toRaw ? new Date(toRaw) : null

      const sameYear =
        fromDate &&
        toDate &&
        fromDate.getFullYear() === toDate.getFullYear() &&
        fromDate.getMonth() === 0 &&
        fromDate.getDate() === 1 &&
        toDate.getMonth() === 11 &&
        toDate.getDate() === 31

      if (sameYear) return String(fromDate.getFullYear())

      const from = fromDate ? format(fromDate) : ''
      const to = toDate ? format(toDate) : ''

      if (from && !to) return from
      if (!from && to) return to
      if (from === to) return from

      return `${from} – ${to}`
    }

    const months = getMonthRange()

    const costByMonth = months.reduce((acc, monthKey) => {
      acc[monthKey] = 0
      return acc
    }, {})

    activeReceipts.forEach((receipt) => {
      const key = String(receipt?.date || '').slice(0, 7)
      if (costByMonth[key] !== undefined) costByMonth[key] += toNum(receipt?.total_price)
    })

    activeServices.forEach((service) => {
      const key = String(service?.date || '').slice(0, 7)
      if (costByMonth[key] !== undefined) costByMonth[key] += toNum(service?.totalCost)
    })

    const costsChartData = months.map((month) => ({
      month,
      cost: Number(costByMonth[month] || 0)
    }))

    const emissionData = vehiclesStats.map((vehicle) => ({
      id: vehicle.id,
      vehicleId: String(vehicle.id),
      name: vehicle.name,
      licensePlate: vehicle.licensePlate || '',
      spz: vehicle.spz || '',
      totalKm: Number(vehicle.km || 0),
      emissionsPerKm: Number(vehicle.co2PerKm || 0),
      totalEmissionKg: Number(vehicle.co2Kg || 0)
    }))

    return {
      statsMode,
      totalTrips,
      totalKm,
      totalCost,
      totalReceiptCost,
      totalServiceCost,
      totalFuel,
      totalElectricKwh,
      avgElectricPrice,
      electricKwhPer100Km,
      electricCostPer100Km,
      chargingStats,
      avgConsumption,
      avgCostPerKm,
      totalCo2Kg,
      co2PerKm,
      vehiclesStats,
      tripsChartData: normalizedTripsChartData,
      tripsMaxValue,
      tripsTicks,
      tripsLegend: Object.values(chartVehicleMap),
      tripsPeriodText: formatPeriodText(),
      costsChartData,
      emissionData
    }
  }, [filterCriteria, i18n.language, receipts, services, trips, vehicles, statsMode])

  const handleDragStart = (e) => {
    if (e.button !== undefined && e.button !== 0) return

    e.preventDefault()
    e.currentTarget.setPointerCapture?.(e.pointerId)

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      modalX: modalPosition.x,
      modalY: modalPosition.y
    }
  }

  const handleDragMove = (e) => {
    if (!dragRef.current || !modalRef.current) return

    const modalRect = modalRef.current.getBoundingClientRect()
    const requestedX = dragRef.current.modalX + e.clientX - dragRef.current.startX
    const requestedY = dragRef.current.modalY + e.clientY - dragRef.current.startY
    const maxX = Math.max(0, (window.innerWidth - modalRect.width) / 2)
    const maxY = Math.max(0, (window.innerHeight - modalRect.height) / 2)

    setModalPosition({
      x: Math.max(-maxX, Math.min(maxX, requestedX)),
      y: Math.max(-maxY, Math.min(maxY, requestedY))
    })
  }

  const handleDragEnd = (e) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId)
    dragRef.current = null
  }

  if (!open) return null

  const fmtNum = (value, digits = 0) => Number(value || 0).toLocaleString('sk-SK', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })

  const statsExportOptions = {
    activeTab,
    stats,
    selectedVehicleName,
    language: i18n.language,
    t
  }

  const visibleTabs = tabs.filter((tab) => statsMode === 'fuel' || tab.key !== 'emissions')

  const getModeButtonStyle = (mode) => ({
    ...tabButton,
    background: statsMode === mode ? '#2da9a4' : '#f5f5f5',
    boxShadow: statsMode === mode
      ? '0 2px 18px rgba(16,124,107,0.65), 0 0 6px rgba(16,124,107,0.55)'
      : '0 1px 8px rgba(0,0,0,0.30)'
  })

  const handleModeMouseLeave = (e, mode) => {
    const isActive = statsMode === mode

    e.currentTarget.style.background = isActive ? '#3BC6C4' : '#f5f5f5'
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = isActive
      ? '0 2px 18px rgba(16,124,107,0.65), 0 0 6px rgba(16,124,107,0.55)'
      : '0 1px 8px rgba(0,0,0,0.30)'
  }

  return (
    <div onClick={onClose} style={overlay}>
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          ...modal,
          left: `${modalPosition.x}px`,
          top: `${modalPosition.y}px`
        }}
      >
        <div
          style={headerRow}
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
        >
          <h2 style={{ margin: 0, fontSize: 24 }}>{t('TRIPLOG.STATISTICS.TITLE')}</h2>
        </div>

        <div style={modeTabsRow}>
          <button
            type="button"
            onClick={() => setStatsMode('fuel')}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2da9a4'
            }}
            onMouseLeave={(e) => handleModeMouseLeave(e, 'fuel')}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.96)'
              e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
              e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
            }}
            style={getModeButtonStyle('fuel')}
          >
            {t('TRIPLOG.STATISTICS.MODE_FUEL')}
          </button>

          <button
            type="button"
            onClick={() => {
              setStatsMode('electric')
              if (activeTab === 'emissions') setActiveTab('summary')
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#2da9a4'
            }}
            onMouseLeave={(e) => handleModeMouseLeave(e, 'electric')}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.96)'
              e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
              e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
            }}
            style={getModeButtonStyle('electric')}
          >
            {t('TRIPLOG.STATISTICS.MODE_EV')}
          </button>
        </div>

        <div style={tabsRow}>
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2da9a4'
              }}
              onMouseLeave={(e) => {
                const isActive = activeTab === tab.key

                e.currentTarget.style.background = isActive ? '#3BC6C4' : '#f5f5f5'
                e.currentTarget.style.transform = 'scale(1)'
                e.currentTarget.style.boxShadow = isActive
                  ? '0 2px 18px rgba(16,124,107,0.65), 0 0 6px rgba(16,124,107,0.55)'
                  : '0 1px 8px rgba(0,0,0,0.30)'
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'scale(0.96)'
                e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)'
                e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
              }}
              style={{
                ...tabButton,
                background: activeTab === tab.key ? '#2da9a4' : '#f5f5f5',
                boxShadow: activeTab === tab.key
                  ? '0 2px 18px rgba(16,124,107,0.65), 0 0 6px rgba(16,124,107,0.55)'
                  : '0 1px 8px rgba(0,0,0,0.30)'
              }}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        <div style={{ padding: '0 20px 12px 20px', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <GenericListFilter
            value={filterCriteria}
            onChange={setFilterCriteria}
            vehicles={vehicleOptions}
            labels={filterLabels}
          />

          <ExportButton
            dropdownTop={36}
            buttonStyle={{
              borderRadius: 8,
              height: 34,
              padding: '0px 14px',
              marginTop: 0,
              marginBottom: 10,
              fontSize: 14,
              lineHeight: '32px',
              fontFamily: 'inherit'
            }}
            onCSV={() => exportCurrentStatsCSV(statsExportOptions)}
            onXLS={() => exportCurrentStatsXLS(statsExportOptions)}
            onPDF={() => exportCurrentStatsPDF(statsExportOptions)}
          />
        </div>

        <div style={body}>
          {isLoading ? (
            <div style={{ padding: 16 }}>{t('LOADING')}...</div>
          ) : (
            <>
              {activeTab === 'summary' && statsMode === 'fuel' && (
                <StatsOverviewModal
                  stats={stats}
                  t={t}
                  fmtNum={fmtNum}
                  filterContext={{ vehicleName: selectedVehicleName }}
                />
              )}

              {activeTab === 'summary' && statsMode === 'electric' && (
                <StatsEvOverviewModal
                  stats={stats}
                  t={t}
                  fmtNum={fmtNum}
                  filterContext={{ vehicleName: selectedVehicleName }}
                />
              )}

              {activeTab === 'trips' && (
                <StatsTripsModal stats={stats} t={t} fmtNum={fmtNum} />
              )}

              {activeTab === 'costs' && (
                <StatsCostsModal
                  stats={stats}
                  t={t}
                  fmtNum={fmtNum}
                  vehicles={vehicles}
                  filterCriteria={filterCriteria}
                  i18n={i18n}
                />
              )}

              {activeTab === 'emissions' && statsMode === 'fuel' && (
                <StatsEmissionsModal
                  stats={stats}
                  t={t}
                  fmtNum={fmtNum}
                  filterCriteria={filterCriteria}
                />
              )}

              {activeTab === 'vehicles' && statsMode === 'fuel' && (
                <StatsVehiclesModal
                  stats={stats}
                  t={t}
                  fmtNum={fmtNum}
                  filterCriteria={filterCriteria}
                />
              )}

              {activeTab === 'vehicles' && statsMode === 'electric' && (
                <StatsEvVehiclesModal
                  stats={stats}
                  t={t}
                  fmtNum={fmtNum}
                  filterCriteria={filterCriteria}
                />
              )}
            </>
          )}
        </div>

        <div style={footer}>
          <div
            style={{
              fontSize: 16,
              color: '#555',
              textAlign: 'left',
              flex: 1,
              paddingRight: 24,
              lineHeight: 1.4
            }}
          >
            <Trans
              i18nKey="STATS_IDEAS"
              components={{
                contact: (
                  <button
                    type="button"
                    onClick={() => setOpenContact(true)}
                    onMouseEnter={() => setContactLinkHover(true)}
                    onMouseLeave={() => setContactLinkHover(false)}
                    style={{
                      display: 'inline',
                      verticalAlign: 'baseline',
                      background: contactLinkHover ? 'rgba(0,0,0,0.85)' : 'transparent',
                      color: contactLinkHover ? '#E7DFEC' : 'inherit',
                      border: 0,
                      borderRadius: 6,
                      padding: 0,
                      margin: 0,
                      cursor: 'pointer',
                      font: 'inherit',
                      lineHeight: 'inherit',
                      textDecoration: 'underline',
                      textDecorationThickness: '1px',
                      textUnderlineOffset: '2px',
                      boxShadow: contactLinkHover
                        ? '0 0 0 4px rgba(0,0,0,0.85), 0 8px 20px rgba(0,0,0,0.35)'
                        : 'none',
                      transition: 'background 0.12s ease, color 0.12s ease, box-shadow 0.12s ease'
                    }}
                  />
                )
              }}
            />
          </div>

          <button
            style={modalSecondaryButtonStyle(
              pressedBtn,
              'close',
              hoverBtn === 'close'
            )}
            onMouseEnter={() => setHoverBtn('close')}
            onMouseLeave={() => {
              setHoverBtn(null)
              setPressedBtn(null)
            }}
            onMouseDown={() => setPressedBtn('close')}
            onMouseUp={() => setPressedBtn(null)}
            onClick={onClose}
          >
            {t('CLOSE')}
          </button>
        </div>
      </div>

      <ContactModal
        open={openContact}
        onClose={() => setOpenContact(false)}
      />
    </div>
  )
}

const overlay = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2200,
  padding: 24
}

const modal = {
  position: 'relative',
  background: '#ededed',
  border: '2px solid #1e3f44',
  borderRadius: 12,
  boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
  width: 'min(1150px, 96vw)',
  maxHeight: '92vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}

const headerRow = {
  padding: '16px 20px 8px 20px',
  cursor: 'move',
  userSelect: 'none',
  touchAction: 'none'
}

const modeTabsRow = {
  display: 'flex',
  gap: 8,
  padding: '0 20px 10px 20px'
}

const tabsRow = {
  display: 'flex',
  gap: 8,
  padding: '0 20px 12px 20px',
  flexWrap: 'wrap'
}

const tabButton = {
  background: '#f5f5f5',
  border: '1px solid #1e3f44',
  color: '#000',
  borderRadius: 8,
  padding: '8px 14px',
  paddingTop: 8,
  paddingBottom: 6,
  lineHeight: '18px',
  cursor: 'pointer',
  fontWeight: 700,
  boxShadow: '0 1px 8px rgba(0,0,0,0.30)',
  transition: 'transform 0.05s ease, box-shadow 0.1s ease'
}

const body = {
  padding: '0 20px 16px 20px',
  overflowY: 'auto'
}

const footer = {
  padding: '10px 20px 16px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  boxSizing: 'border-box'
}

export default StatsModal